import RouterFactory from '@trax/classes/RouterFactory'

// Layout.
import AuthLayout from './layout/AuthLayout.vue';
import FullPageLayout from './layout/FullPageLayout.vue';
import SideMenuLayout from './layout/SideMenuLayout.vue';

// General Views.
import NotFoundPage from './pages/NotFoundPage.vue';
import UnauthorizedPage from './pages/UnauthorizedPage.vue';
import MaintenancePage from './pages/MaintenancePage.vue';
import ErrorPage from './pages/ErrorPage.vue';

// Auth Views.
import LoginPage from './pages/auth/LoginPage.vue';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage.vue';
import DefinePasswordPage from './pages/auth/DefinePasswordPage.vue';
import MyAccountPage from './pages/auth/MyAccountPage.vue';
import ChangePassword from './pages/auth/ChangePasswordPage.vue';
import PrivacyPage from './pages/PrivacyPage.vue';

// Account Views.
import AdminUsersPage from './pages/accounts/UsersPage.vue';
import AdminUserPage from './pages/accounts/UserPage.vue';
import AdminCohortsPage from './pages/accounts/CohortsPage.vue';
import AdminCohortPage from './pages/accounts/CohortPage.vue';
import AdminRolesPage from './pages/accounts/RolesPage.vue';
import AdminOwnersPage from './pages/accounts/OwnersPage.vue';
import AdminEntitiesPage from './pages/accounts/EntitiesPage.vue';

// Training Views.
import AdminCompetenciesPage from './pages/training/CompetenciesPage.vue';
import AdminCompetencyGridsPage from './pages/training/CompetencyGridsPage.vue';
import AdminGridPage from './pages/training/CompetencyGridPage.vue';
import AdminDegreesPage from './pages/training/DegreesPage.vue';
import AdminDegreePage from './pages/training/DegreePage.vue';
import AdminLogPage from './pages/training/LogPage.vue';
import AdminRegistrationsPage from './pages/training/RegistrationsPage.vue';
import AdminRegistrationPage from './pages/training/RegistrationPage.vue';

// Admin import views.
import AdminImportUsersPage from './pages/accounts/ImportUsersPage.vue';
import AdminImportCohortsPage from './pages/accounts/ImportCohortsPage.vue';

// Settings view.
import AdminAstralImportPage from './pages/settings/AstralImportPage.vue';

// Admin clear views.
import AdminClearUsersPage from './pages/accounts/ClearUsersPage.vue';
import AdminClearCohortsPage from './pages/accounts/ClearCohortsPage.vue';
import AdminClearCompetencyGridsPage from './pages/training/ClearCompetencyGridsPage.vue';
import AdminClearDegreesPage from './pages/training/ClearDegreesPage.vue';
import AdminClearRegistrationsPage from './pages/training/ClearRegistrationsPage.vue';

// Home Views.
import HomePage from './pages/HomePage.vue';

// Dashboard views: simulations
import DashboardLogsPage from './pages/dashboard/LogsPage.vue';
import DashboardLogPage from './pages/dashboard/LogPage.vue';
import DashboardLogSummaryPage from './pages/dashboard/LogSummaryPage.vue';

// Dashboard views: sessions
import DashboardSessionsPage from './pages/dashboard/SessionsPage.vue';
import DashboardSessionsRegistrationPage from './pages/dashboard/SessionsRegistrationPage.vue';
import DashboardSessionsRegistrationLearnerPage from './pages/dashboard/SessionsRegistrationLearnerPage.vue';
import DashboardSessionConfigurationPage from './pages/dashboard/SessionConfigurationPage.vue';
import DashboardSessionObservationPage from './pages/dashboard/SessionObservationPage.vue';
import DashboardSessionEvaluationPage from './pages/dashboard/SessionEvaluationPage.vue';
import DashboardSessionSummaryPage from './pages/dashboard/SessionSummaryPage.vue';

// Dashboard views: grades
import DashboardGradesPage from './pages/dashboard/GradesPage.vue';
import DashboardGradesRegistrationPage from './pages/dashboard/GradesRegistrationPage.vue';

// Dashboard views: issues
import DashboardIssuesPage from './pages/dashboard/IssuesPage.vue';
import DashboardIssuesOfSimulationPage from './pages/dashboard/IssuesOfSimulationPage.vue';

// Offline dashboard pages.
import OfflineDashboardPage from './pages/dashboard/OfflineDashboardPage.vue';
import OfflineTrashPage from './pages/dashboard/OfflineTrashPage.vue';
import OfflineDisabledPage from './pages/OfflineDisabledPage.vue';

function getRoutes(auth) {

    // Used for local permissions implementation.
    const permissions = Vue.prototype.$permissions

    return [
        
        // Auth pages.
        {
            path: "/socrates/ui",
            redirect: "/socrates/ui/login",
            component: AuthLayout,
            children: [
                {
                    path: "login",
                    name: "login",
                    beforeEnter: auth.ifNotAuthenticated,
                    component: LoginPage
                },
                {
                    path: "reset-password",
                    name: "forgot-password",
                    beforeEnter: auth.ifNotAuthenticated,
                    component: ForgotPasswordPage
                },
                {
                    path: "reset-password/:token",
                    name: "define-password",
                    beforeEnter: auth.ifNotAuthenticated,
                    component: DefinePasswordPage
                },
            ]
        },

        // Offline pages.
        {
            path: "/socrates/ui",
            component: SideMenuLayout,
            children: [
                {
                    path: "offline/dashboard",
                    name: "offline-dashboard",
                    beforeEnter: auth.ifAuthenticated,
                    component: OfflineDashboardPage
                },
                {
                    path: "offline/trash",
                    name: "offline-trash",
                    beforeEnter: auth.ifAuthenticated,
                    component: OfflineTrashPage
                },
                {
                    path: "offline/disabled",
                    name: "offline-disabled",
                    beforeEnter: auth.ifAuthenticated,
                    component: OfflineDisabledPage
                },
            ]
        },

        // Home & profile.
        {
            path: "/socrates/ui",
            component: SideMenuLayout,
            children: [
                {
                    path: "home",
                    name: "home",
                    beforeEnter: permissions.redirectToHome,
                    component: HomePage
                },
                {
                    path: "my-account",
                    name: "my-account",
                    beforeEnter: (to, from, next) => {
                        auth.ifAuthenticated(to, from, next, 'home')
                    },
                    component: MyAccountPage
                },
                {
                    path: "change-password",
                    name: "change-password",
                    beforeEnter: (to, from, next) => {
                        auth.ifAuthenticated(to, from, next, 'home')
                    },
                    component: ChangePassword
                },
                {
                    path: "privacy",
                    name: "privacy",
                    beforeEnter: (to, from, next) => {
                        auth.ifAuthenticated(to, from, next, 'home')
                    },
                    component: PrivacyPage
                },
            ]
        },

        // Accounts management.
        {
            path: "/socrates/ui/accounts",
            component: SideMenuLayout,
            children: [
                {
                    path: "users",
                    name: "admin-users",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasOnePermission(['user.manage', 'user.manage-pilots', 'user.supervise'], to, from, next, 'home')
                    },
                    component: AdminUsersPage
                },
                {
                    path: "users/clear",
                    name: "clear-users",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasAllPermissions(['user.manage', 'admin.delete-personal-data'], to, from, next, 'home')
                    },
                    component: AdminClearUsersPage
                },
                {
                    path: "users/import",
                    name: "import-users",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasAllPermissions(['user.manage'], to, from, next, 'home')
                    },
                    component: AdminImportUsersPage
                },
                {
                    path: "users/:id",
                    name: "admin-user",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasOnePermission(['user.manage', 'user.manage-pilots', 'user.supervise'], to, from, next, 'home')
                    },
                    component: AdminUserPage
                },
                {
                    path: "cohorts",
                    name: "admin-cohorts",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasPermission('cohort.manage', to, from, next, 'home')
                    },
                    component: AdminCohortsPage
                },
                {
                    path: "cohorts/clear",
                    name: "clear-cohorts",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasAllPermissions(['cohort.manage', 'admin.delete-personal-data'], to, from, next, 'home')
                    },
                    component: AdminClearCohortsPage
                },
                {
                    path: "cohorts/import",
                    name: "import-cohorts",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasAllPermissions(['cohort.manage'], to, from, next, 'home')
                    },
                    component: AdminImportCohortsPage
                },
                {
                    path: "cohorts/:id",
                    name: "admin-cohort",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasPermission('cohort.manage', to, from, next, 'home')
                    },
                    component: AdminCohortPage
                },
                {
                    path: "roles",
                    name: "admin-roles",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasPermission('role.manage', to, from, next, 'home')
                    },
                    component: AdminRolesPage
                },
                {
                    path: "owners",
                    name: "admin-owners",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasPermission('owner.manage', to, from, next, 'home')
                    },
                    component: AdminOwnersPage
                },
                {
                    path: "entities",
                    name: "admin-entities",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasPermission('entity.manage', to, from, next, 'home')
                    },
                    component: AdminEntitiesPage
                },
            ]
        },

        // Training management.
        {
            path: "/socrates/ui/training",
            component: SideMenuLayout,
            children: [
                {
                    path: "competencies",
                    name: "admin-competencies",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasPermission('competency.manage', to, from, next, 'home')
                    },
                    component: AdminCompetenciesPage
                },
                {
                    path: "competency-grids",
                    name: "admin-competency-grids",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasPermission('grid.manage', to, from, next, 'home')
                    },
                    component: AdminCompetencyGridsPage
                },
                {
                    path: "competency-grids/clear",
                    name: "clear-competency-grids",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasAllPermissions(['grid.manage', 'admin.delete-archived-content'], to, from, next, 'home')
                    },
                    component: AdminClearCompetencyGridsPage
                },
                {
                    path: "competency-grids/:id",
                    name: "admin-competency-grid",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasPermission('grid.manage', to, from, next, 'home')
                    },
                    component: AdminGridPage
                },
                {
                    path: "degrees",
                    name: "admin-degrees",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasPermission('degree.manage', to, from, next, 'home')
                    },
                    component: AdminDegreesPage
                },
                {
                    path: "degrees/clear",
                    name: "clear-degrees",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasAllPermissions(['degree.manage', 'admin.delete-archived-content'], to, from, next, 'home')
                    },
                    component: AdminClearDegreesPage
                },
                {
                    path: "degrees/:id",
                    name: "admin-degree",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasPermission('degree.manage', to, from, next, 'home')
                    },
                    component: AdminDegreePage
                },
                {
                    path: "degrees/:degreeId/simulations/:simulationId",
                    name: "admin-log",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasPermission('degree.manage', to, from, next, 'home')
                    },
                    component: AdminLogPage
                },
                {
                    path: "registrations",
                    name: "admin-registrations",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasPermission('registration.manage', to, from, next, 'home')
                    },
                    component: AdminRegistrationsPage
                },
                {
                    path: "registrations/clear",
                    name: "clear-registrations",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasAllPermissions(['registration.manage', 'admin.delete-personal-data'], to, from, next, 'home')
                    },
                    component: AdminClearRegistrationsPage
                },
                {
                    path: "registrations/:id",
                    name: "admin-registration",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasPermission('registration.manage', to, from, next, 'home')
                    },
                    component: AdminRegistrationPage
                },
            ]
        },

        // Settings.
        {
            path: "/socrates/ui/settings",
            component: SideMenuLayout,
            children: [
                {
                    path: "astral-import",
                    name: "astral-import",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasPermission('admin', to, from, next, 'home')
                    },
                    component: AdminAstralImportPage
                },
            ]
        },

        // New SOCRATES dashboard.
        {
            path: "/socrates/ui/dashboard",
            component: SideMenuLayout,
            children: [
                {
                    path: "issues",
                    name: "dashboard-issues",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasOnePermission([
                            'dashboard.access-issues-from-dashboard',
                        ], to, from, next, 'home')
                    },
                    component: DashboardIssuesPage
                },
                {
                    path: "issues/registrations/:registrationId/simulations/:simulationId",
                    name: "dashboard-simulation-issues",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasOnePermission([
                            'dashboard.access-issues-from-dashboard',
                        ], to, from, next, 'home')
                    },
                    component: DashboardIssuesOfSimulationPage
                },
                {
                    path: "logs",
                    name: "dashboard-logs",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasOnePermission([
                            'dashboard.access-pilot-dashboard',
                        ], to, from, next, 'home')
                    },
                    component: DashboardLogsPage
                },
                {
                    // There is no offline redirection here because we want to be able to access to the offline log.
                    // In the page, the 444 error is checked and it redirects to the offline dashboard.
                    path: "logs/registrations/:registrationId/simulations/:simulationId",
                    name: "dashboard-log",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasOnePermission([
                            'dashboard.access-pilot-dashboard',
                        ], to, from, next)
                    },
                    component: DashboardLogPage
                },
                {
                    // There is no offline redirection here because we want to be able to access to the offline log.
                    // In the page, the 444 error is checked and it redirects to the offline dashboard.
                    path: "logs/registrations/:registrationId/simulations/:simulationId/summary",
                    name: "dashboard-log-summary",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasOnePermission([
                            'dashboard.access-pilot-dashboard',
                        ], to, from, next)
                    },
                    component: DashboardLogSummaryPage
                },
                {
                    path: "sessions",
                    name: "dashboard-sessions",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasOnePermission([
                            'dashboard.access-instructor-dashboard',
                            'dashboard.access-internal-observer-dashboard',
                            'dashboard.access-restricted-internal-observer-dashboard',
                            'dashboard.access-external-observer-dashboard',
                            'dashboard.access-learner-dashboard',
                        ], to, from, next, 'home')
                    },
                    component: DashboardSessionsPage
                },
                {
                    path: "sessions/registrations/:registrationId",
                    name: "dashboard-registration-sessions",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasOnePermission([
                            'dashboard.access-instructor-dashboard',
                            'dashboard.access-internal-observer-dashboard',
                            'dashboard.access-restricted-internal-observer-dashboard',
                            'dashboard.access-external-observer-dashboard',
                            'dashboard.access-learner-dashboard',
                        ], to, from, next, 'home')
                    },
                    component: DashboardSessionsRegistrationPage
                },
                {
                    path: "sessions/registrations/:registrationId/learners/:learnerId",
                    name: "dashboard-registration-learner-sessions",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasOnePermission([
                            'dashboard.access-instructor-dashboard',
                            'dashboard.access-internal-observer-dashboard',
                            'dashboard.access-restricted-internal-observer-dashboard',
                            'dashboard.access-external-observer-dashboard',
                            'dashboard.access-learner-dashboard',
                        ], to, from, next, 'home')
                    },
                    component: DashboardSessionsRegistrationLearnerPage
                },
                {
                    // There is no offline redirection here because we want to be able to access to the offline log.
                    // In the page, the 444 error is checked and it redirects to the offline dashboard.
                    path: "sessions/:id/configuration",
                    name: "dashboard-session-configuration",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasOnePermission([
                            'dashboard.access-instructor-dashboard',
                        ], to, from, next)
                    },
                    component: DashboardSessionConfigurationPage
                },
                {
                    // There is no offline redirection here because we want to be able to access to the offline log.
                    // In the page, the 444 error is checked and it redirects to the offline dashboard.
                    path: "sessions/:id/observation",
                    name: "dashboard-session-observation",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasOnePermission([
                            'dashboard.access-instructor-dashboard',
                        ], to, from, next)
                    },
                    component: DashboardSessionObservationPage
                },
                {
                    // There is no offline redirection here because we want to be able to access to the offline log.
                    // In the page, the 444 error is checked and it redirects to the offline dashboard.
                    path: "sessions/:id/evaluation",
                    name: "dashboard-session-evaluation",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasOnePermission([
                            'dashboard.access-instructor-dashboard',
                        ], to, from, next)
                    },
                    component: DashboardSessionEvaluationPage
                },
                {
                    // There is no offline redirection here because we want to be able to access to the offline log.
                    // In the page, the 444 error is checked and it redirects to the offline dashboard.
                    path: "sessions/:id/summary",
                    name: "dashboard-session-summary",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasOnePermission([
                            'dashboard.access-instructor-dashboard',
                            'dashboard.access-internal-observer-dashboard',
                            'dashboard.access-restricted-internal-observer-dashboard',
                            'dashboard.access-external-observer-dashboard',
                            'dashboard.access-learner-dashboard',
                        ], to, from, next)
                    },
                    component: DashboardSessionSummaryPage
                },
                {
                    path: "grades",
                    name: "dashboard-grades",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasOnePermission([
                            'dashboard.access-grades-from-dashboard',
                        ], to, from, next, 'home')
                    },
                    component: DashboardGradesPage
                },
                {
                    path: "grades/registrations/:registrationId",
                    name: "dashboard-registration-grades",
                    beforeEnter: (to, from, next) => {
                        auth.ifHasOnePermission([
                            'dashboard.access-grades-from-dashboard',
                        ], to, from, next, 'home')
                    },
                    component: DashboardGradesRegistrationPage
                },
            ]
        },

        // Special pages.
        {
            path: "/socrates/ui",
            component: FullPageLayout,
            children: [
                {
                    path: "unauthorized",
                    name: "unauthorized",
                    component: UnauthorizedPage,
                },
                {
                    path: "maintenance",
                    name: "maintenance",
                    component: MaintenancePage,
                },
                {
                    path: "error",
                    name: "error",
                    component: ErrorPage,
                },
                {
                    path: "*",
                    name: "unknown",
                    component: NotFoundPage,
                },
            ]
        },
    ]
}

export default {

    routes() {
        return getRoutes(Vue.prototype.$auth)
    },

    router() {
        return RouterFactory.make(this.routes(), 'nav-item active')
    },
}

